import { EmptyState } from '../../components/EmptyState';
import { Skeleton } from '../../components/Skeleton';
import { useAccountingView } from '../../hooks/useAccountingView';

import styles from './Accounting.module.css';
import { ACCOUNTING_EMPTY } from './empty';
import { MoneyCell, commitsSub } from './MoneyCell';
import { INVESTMENT_LABELS, fmtPct, rollupInvestmentByFunction } from './rollups';
import type { InvestmentType } from './types';

// Investment areas are always the columns (see also TeamsView); business
// functions are the rows.
const COLS: Array<{ key: InvestmentType }> = [
  { key: 'ktlo' },
  { key: 'enablement' },
  { key: 'growth' },
];

/**
 * Report 1: Business Function × Investment Type.
 * Dollars across all commits in the period, grouped by classification.
 */
export function InvestmentByFunctionView() {
  const { data, isPending, error } = useAccountingView();
  if (isPending) return <Skeleton title='Investment × Function' rows={5} />;
  if (error) return <div className={styles.errorBox}>Failed to load accounting view: {error.message}</div>;
  if (!data) return null;
  if (data.commits.length === 0) return <EmptyState {...ACCOUNTING_EMPTY} />;

  const r = rollupInvestmentByFunction(data);
  const bfs = data.business_functions;

  return (
    <table className={styles.matrix}>
      <thead>
        <tr>
          <th>Business Function</th>
          {COLS.map(({ key }) => (
            <th key={key}>{INVESTMENT_LABELS[key]}</th>
          ))}
          <th>Total</th>
        </tr>
      </thead>
      <tbody>
        {bfs.map((bf) => {
          const rowTotal = r.functionTotals[bf.id];
          return (
            <tr key={bf.id}>
              <td>{bf.display}</td>
              {COLS.map(({ key }) => {
                const cell = r.cells[`${key}|${bf.id}`];
                return (
                  <td key={key}>
                    <MoneyCell cost={cell?.cost_usd ?? 0} sub={commitsSub(cell?.count ?? 0)} />
                  </td>
                );
              })}
              <td>
                <MoneyCell
                  cost={rowTotal?.cost_usd ?? 0}
                  sub={`${fmtPct(rowTotal?.cost_usd ?? 0, r.grandTotal.cost_usd)} of total`}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr>
          <td>Total</td>
          {COLS.map(({ key }) => {
            const colTotal = r.investmentTotals[key];
            return (
              <td key={key}>
                <MoneyCell cost={colTotal.cost_usd} sub={commitsSub(colTotal.count)} />
              </td>
            );
          })}
          <td>
            <MoneyCell cost={r.grandTotal.cost_usd} sub={commitsSub(r.grandTotal.count)} />
          </td>
        </tr>
      </tfoot>
    </table>
  );
}
